import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Plus, Search, Trash2, MessageCircle, Phone, Mail,
  Eye, Edit2, Loader2, ChevronDown, ChevronRight,
  Send, Clock, UserPlus, UserCheck, UserX, FileText, Briefcase,
  Zap, ZapOff, Bell, BellOff, Save, CheckCircle
} from 'lucide-react';
import WhatsAppConversation from '../components/WhatsAppConversation';

const STATI = [
  { value: 'nuovo', label: 'Nuovo', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
  { value: 'contattato', label: 'Contattato', color: 'bg-amber-100 text-amber-700', icon: Phone },
  { value: 'proposta_inviata', label: 'Proposta Inviata', color: 'bg-purple-100 text-purple-700', icon: Send },
  { value: 'in_attesa', label: 'In Attesa', color: 'bg-orange-100 text-orange-700', icon: Clock },
  { value: 'convertito', label: 'Convertito', color: 'bg-green-100 text-green-700', icon: UserCheck },
  { value: 'perso', label: 'Perso', color: 'bg-red-100 text-red-700', icon: UserX },
];
const INTERESSI_ASSICURATIVI = [
  'Polizza Auto', 'Polizza Casa', 'Polizza Vita', 'Polizza Salute',
  'Consulenza Generica', 'Altro Assicurativo'
];
const INTERESSI_CREDITO = [
  'Cessione Quinto Pensione', 'Cessione Quinto Stipendio', 'Prestito Personale',
  'Finanziamento', 'Altro Credito'
];
const INTERESSI = [...INTERESSI_ASSICURATIVI, ...INTERESSI_CREDITO];
const isInteresseCredito = (i) => INTERESSI_CREDITO.includes(i);
const FONTI = ['Passaparola', 'Social Media', 'Sito Web', 'Telefono', 'Email', 'Evento', 'Segnalazione', 'Altro'];
const TEMPLATES = [
  { value: 'default', label: 'Follow-up standard' },
  { value: 'primo_contatto', label: 'Primo contatto' },
  { value: 'sollecito', label: 'Sollecito proposta' },
];

const EMPTY_FORM = {
  nome: '', cognome: '', telefono: '', email: '',
  interesse: '', fonte: '', note: '', stato: 'nuovo',
  data_followup: '', assigned_to: '',
};

const getStatoStyle = (stato) => STATI.find(s => s.value === stato) || STATI[0];

export default function Leads() {
  const { user, isAdmin } = useAuth();
  // Determina contesto dalla query string: ?tipo=credito (sezione Credito) o assenza (assicurativo+tutto)
  const params = new URLSearchParams(window.location.search);
  const tipoContesto = params.get('tipo') || 'assicurativo'; // 'credito' | 'assicurativo'
  const isCreditoContext = tipoContesto === 'credito';
  const interessiDisponibili = isCreditoContext ? INTERESSI_CREDITO : INTERESSI_ASSICURATIVI;

  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStato, setFilterStato] = useState('');
  const [filterInteresse, setFilterInteresse] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM, interesse: interessiDisponibili[0] });
  const [sendingWA, setSendingWA] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(true);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStato) params.append('stato', filterStato);
      if (filterInteresse) params.append('interesse', filterInteresse);
      if (search) params.append('search', search);
      const r = await api.get(`/leads?${params}`);
      // Filtro client-side per contesto: nei lead credito mostra solo lead con interesse credito (e viceversa)
      const filtered = r.data.filter(l => {
        if (!l.interesse) return !isCreditoContext; // lead senza interesse vanno solo nella vista assicurativa
        return isCreditoContext ? isInteresseCredito(l.interesse) : !isInteresseCredito(l.interesse);
      });
      setLeads(filtered);
    } catch {} finally { setLoading(false); }
  }, [filterStato, filterInteresse, search, isCreditoContext]);

  const fetchStats = useCallback(async () => {
    try { const r = await api.get('/leads/stats/overview'); setStats(r.data); } catch {}
  }, []);

  const fetchUsers = useCallback(async () => {
    try { const r = await api.get('/users'); setUsers(r.data); } catch {}
  }, []);

  const fetchAutoStatus = useCallback(async () => {
    try { const r = await api.get('/leads/automation/status'); setAutoEnabled(r.data.enabled); } catch {}
  }, []);

  useEffect(() => { fetchLeads(); fetchStats(); fetchUsers(); fetchAutoStatus(); }, [fetchLeads, fetchStats, fetchUsers, fetchAutoStatus]);

  const F = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openNew = () => { setEditingLead(null); setForm({ ...EMPTY_FORM }); setDialogOpen(true); };
  const openEdit = (l) => {
    setEditingLead(l);
    const f = {};
    Object.keys(EMPTY_FORM).forEach(k => { f[k] = l[k] || EMPTY_FORM[k]; });
    setForm(f);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.nome && !form.cognome) { toast.error('Inserisci almeno il nome'); return; }
    try {
      if (editingLead) {
        await api.patch(`/leads/${editingLead.id}`, form);
        toast.success('Lead aggiornato');
      } else {
        await api.post('/leads', form);
        toast.success('Lead creato');
      }
      setDialogOpen(false); fetchLeads(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo lead?')) return;
    try { await api.delete(`/leads/${id}`); fetchLeads(); fetchStats(); } catch {}
  };

  const handleChangeStato = async (lead, stato) => {
    try { await api.patch(`/leads/${lead.id}`, { stato }); fetchLeads(); fetchStats(); } catch {}
  };

  const sendWhatsApp = async (lead, template = 'default', customMsg = null) => {
    setSendingWA(true);
    try {
      const params = new URLSearchParams({ template });
      if (customMsg) params.append('messaggio_personalizzato', customMsg);
      const r = await api.post(`/leads/${lead.id}/whatsapp?${params}`);
      toast.success('Messaggio WhatsApp inviato!');
      const updated = await api.get(`/leads/${lead.id}`);
      setSelectedLead(updated.data);
      fetchLeads(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore invio WhatsApp'); }
    finally { setSendingWA(false); }
  };

  const convertToPratica = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/converti-pratica`);
      toast.success('Lead convertito in pratica!');
      setDetailOpen(false); fetchLeads(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const convertToPreventivo = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/converti-preventivo`);
      toast.success('Lead convertito in preventivo!');
      setDetailOpen(false); fetchLeads(); fetchStats();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const toggleAutoGlobal = async () => {
    try {
      const r = await api.post('/leads/automation/toggle');
      setAutoEnabled(r.data.enabled);
      toast.success(r.data.enabled ? 'Automazione attivata' : 'Automazione disattivata');
    } catch (e) { toast.error('Errore'); }
  };

  const toggleAutoLead = async (lead) => {
    try {
      const r = await api.post(`/leads/${lead.id}/toggle-auto-followup`);
      toast.success(r.data.auto_followup_enabled ? 'Auto follow-up attivato' : 'Auto follow-up disattivato');
      fetchLeads();
      if (selectedLead?.id === lead.id) {
        const updated = await api.get(`/leads/${lead.id}`);
        setSelectedLead(updated.data);
      }
    } catch {}
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const isOverdue = (dateStr) => {
    if (!dateStr) return false;
    return dateStr <= new Date().toISOString().slice(0, 10);
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const followupToday = leads.filter(l =>
    l.data_followup && l.data_followup <= todayStr && !['convertito', 'perso'].includes(l.stato)
  );

  return (
    <div className="p-4 md:p-6 min-h-screen" data-testid="leads-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="leads-title">Lead</h1>
          <p className="text-sm text-slate-500 mt-0.5">Gestisci i tuoi contatti interessati e follow-up</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <button
              onClick={toggleAutoGlobal}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                autoEnabled
                  ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                  : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
              }`}
              title={autoEnabled ? 'Auto follow-up WhatsApp attivo' : 'Auto follow-up WhatsApp disattivato'}
              data-testid="toggle-auto-global"
            >
              {autoEnabled ? <Zap size={13} /> : <ZapOff size={13} />}
              Auto WA {autoEnabled ? 'ON' : 'OFF'}
            </button>
          )}
          <Button onClick={openNew} size="sm" className="bg-slate-800 hover:bg-slate-700 text-white shadow-sm" data-testid="new-lead-btn">
            <Plus size={16} className="mr-1.5" /> Nuovo Lead
          </Button>
        </div>
      </div>

      {/* Follow-up Alert */}
      {followupToday.length > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-3" data-testid="followup-alert">
          <Bell size={18} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-800">
              {followupToday.length} lead da contattare oggi
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              {followupToday.map(l => `${l.nome} ${l.cognome}`).join(', ')}
            </p>
          </div>
          {autoEnabled && (
            <Badge className="bg-green-100 text-green-700 text-[9px] border-0 shrink-0">
              <Zap size={9} className="mr-0.5" /> Auto WA attivo
            </Badge>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 mb-5">
        {[
          { label: 'Totale', value: stats.totale || 0, color: 'text-slate-800' },
          { label: 'Nuovi', value: stats.nuovi || 0, color: 'text-blue-600' },
          { label: 'Contattati', value: stats.contattati || 0, color: 'text-amber-600' },
          { label: 'Proposta', value: stats.proposta_inviata || 0, color: 'text-purple-600' },
          { label: 'In Attesa', value: stats.in_attesa || 0, color: 'text-orange-600' },
          { label: 'Convertiti', value: stats.convertiti || 0, color: 'text-green-600' },
          { label: 'Persi', value: stats.persi || 0, color: 'text-red-600' },
          { label: 'Follow-up', value: stats.followup_oggi || 0, color: 'text-rose-600' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-2.5 text-center">
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[9px] text-slate-400 font-medium uppercase tracking-wider">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Cerca nome, telefono, email..." value={search} onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-200" data-testid="search-leads" />
        </div>
        <select value={filterStato} onChange={e => setFilterStato(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="filter-stato">
          <option value="">Tutti gli stati</option>
          {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={filterInteresse} onChange={e => setFilterInteresse(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white" data-testid="filter-interesse">
          <option value="">Tutti gli interessi</option>
          {interessiDisponibili.map(i => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 font-semibold text-slate-600 text-xs uppercase">Lead</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-600 text-xs uppercase">Interesse</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-600 text-xs uppercase">Fonte</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-600 text-xs uppercase">Stato</th>
                <th className="text-left px-3 py-3 font-semibold text-slate-600 text-xs uppercase">Follow-up</th>
                <th className="px-3 py-3 text-right font-semibold text-slate-600 text-xs uppercase">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="animate-spin mx-auto text-slate-400" size={20} /></td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-400">Nessun lead trovato</td></tr>
              ) : leads.map(l => {
                const statoStyle = getStatoStyle(l.stato);
                const overdue = isOverdue(l.data_followup) && !['convertito', 'perso'].includes(l.stato);
                const autoOn = l.auto_followup_enabled !== false;
                return (
                  <tr key={l.id} className={`hover:bg-slate-50/50 transition-colors ${overdue ? 'bg-red-50/30' : ''}`} data-testid={`lead-${l.id}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => { setSelectedLead(l); setDetailOpen(true); }} className="text-left hover:text-amber-700">
                        <div className="flex items-center gap-1.5">
                          <p className="font-semibold text-slate-800">{l.nome} {l.cognome}</p>
                          {autoOn && autoEnabled && (
                            <span title="Auto follow-up attivo"><Zap size={11} className="text-green-500" /></span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-400">
                          {l.telefono && <span className="flex items-center gap-0.5"><Phone size={10} />{l.telefono}</span>}
                          {l.email && <span className="flex items-center gap-0.5"><Mail size={10} />{l.email}</span>}
                        </div>
                      </button>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-600">{l.interesse || '-'}</td>
                    <td className="px-3 py-3 text-xs text-slate-500">{l.fonte || '-'}</td>
                    <td className="px-3 py-3">
                      <select value={l.stato} onChange={e => handleChangeStato(l, e.target.value)}
                        className={`text-[10px] px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${statoStyle.color}`}
                        data-testid={`stato-${l.id}`}>
                        {STATI.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-3">
                      {l.data_followup ? (
                        <span className={`text-xs font-medium ${overdue ? 'text-red-600 bg-red-50 px-2 py-0.5 rounded-full animate-pulse' : 'text-slate-500'}`}>
                          {l.data_followup}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex gap-0.5 justify-end">
                        {l.telefono && !['convertito', 'perso'].includes(l.stato) && (
                          <button onClick={() => sendWhatsApp(l)} title="Invia follow-up WhatsApp"
                            className="flex items-center gap-1 px-2 py-1 bg-green-50 hover:bg-green-100 rounded-lg text-green-700 text-[10px] font-medium transition-colors"
                            data-testid={`wa-${l.id}`}>
                            <MessageCircle size={12} />
                            <span className="hidden sm:inline">WhatsApp</span>
                          </button>
                        )}
                        <button onClick={() => { setSelectedLead(l); setDetailOpen(true); }}
                          className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-700" data-testid={`view-${l.id}`}><Eye size={14} /></button>
                        <button onClick={() => openEdit(l)}
                          className="p-1.5 hover:bg-blue-50 rounded-lg text-slate-400 hover:text-blue-600" data-testid={`edit-${l.id}`}><Edit2 size={14} /></button>
                        <button onClick={() => handleDelete(l.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500" data-testid={`delete-${l.id}`}><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingLead ? 'Modifica Lead' : 'Nuovo Lead'}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Nome</Label><Input value={form.nome} onChange={e => F('nome', e.target.value)} className="mt-1" data-testid="form-lead-nome" /></div>
              <div><Label className="text-xs text-slate-600">Cognome</Label><Input value={form.cognome} onChange={e => F('cognome', e.target.value)} className="mt-1" data-testid="form-lead-cognome" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Telefono</Label><Input value={form.telefono} onChange={e => F('telefono', e.target.value)} placeholder="+39..." className="mt-1" data-testid="form-lead-telefono" /></div>
              <div><Label className="text-xs text-slate-600">Email</Label><Input value={form.email} onChange={e => F('email', e.target.value)} className="mt-1" data-testid="form-lead-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Interesse</Label>
                <Select value={form.interesse} onValueChange={v => F('interesse', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-lead-interesse"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{interessiDisponibili.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs text-slate-600">Fonte</Label>
                <Select value={form.fonte} onValueChange={v => F('fonte', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-lead-fonte"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{FONTI.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-slate-600">Data Follow-up</Label><Input type="date" value={form.data_followup} onChange={e => F('data_followup', e.target.value)} className="mt-1" data-testid="form-lead-followup" /></div>
              <div><Label className="text-xs text-slate-600">Assegna a</Label>
                <Select value={form.assigned_to} onValueChange={v => F('assigned_to', v)}>
                  <SelectTrigger className="mt-1" data-testid="form-lead-assign"><SelectValue placeholder="Utente" /></SelectTrigger>
                  <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs text-slate-600">Note</Label><Textarea value={form.note} onChange={e => F('note', e.target.value)} rows={2} className="mt-1" data-testid="form-lead-note" /></div>
            <Button onClick={handleSave} className="w-full bg-slate-800 hover:bg-slate-700 text-white" data-testid="save-lead-btn">Salva Lead</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          {selectedLead && (
            <LeadDetail lead={selectedLead} sendingWA={sendingWA}
              onSendWA={(template, msg) => sendWhatsApp(selectedLead, template, msg)}
              onConvertPratica={() => convertToPratica(selectedLead)}
              onConvertPreventivo={() => convertToPreventivo(selectedLead)}
              onEdit={() => { setDetailOpen(false); openEdit(selectedLead); }}
              onToggleAuto={() => toggleAutoLead(selectedLead)}
              formatDate={formatDate}
              autoGlobalEnabled={autoEnabled}
              onLeadRefresh={async () => {
                try {
                  const r = await api.get(`/leads/${selectedLead.id}`);
                  setSelectedLead(r.data);
                  fetchLeads();
                } catch {}
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ═══ Lead Detail Component ═══ */
function LeadDetail({ lead, sendingWA, onSendWA, onConvertPratica, onConvertPreventivo, onEdit, onToggleAuto, formatDate, autoGlobalEnabled, onLeadRefresh }) {
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [customMsg, setCustomMsg] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [showPreventivi, setShowPreventivi] = useState(true);
  const [prevForm, setPrevForm] = useState(null); // null = chiuso, {} = nuovo, {id, ...} = edit
  const [savingPrev, setSavingPrev] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const preventivi = lead.preventivi || [];
  const statoStyle = getStatoStyle(lead.stato);
  const isActive = !['convertito', 'perso'].includes(lead.stato);
  const autoOn = lead.auto_followup_enabled !== false;

  const openNewPrev = () => setPrevForm({
    titolo: '', compagnia: '', ramo: lead.interesse || 'RCA Auto', targa: '',
    importo: '', importo_rata: '', durata_mesi: '', frequenza_pagamento: '', garanzie: '', note: '', valido_fino: '',
    tasso_tan: '', tasso_taeg: '', segnalato_da: '', provvigione_importo: '', provvigione_percentuale: ''
  });
  const savePrev = async () => {
    if (!prevForm) return;
    setSavingPrev(true);
    try {
      const payload = {
        ...prevForm,
        importo: prevForm.importo === '' ? null : parseFloat(prevForm.importo),
        importo_rata: prevForm.importo_rata === '' ? null : parseFloat(prevForm.importo_rata),
        durata_mesi: prevForm.durata_mesi === '' ? null : parseInt(prevForm.durata_mesi, 10),
        tasso_tan: prevForm.tasso_tan === '' || prevForm.tasso_tan == null ? null : parseFloat(prevForm.tasso_tan),
        tasso_taeg: prevForm.tasso_taeg === '' || prevForm.tasso_taeg == null ? null : parseFloat(prevForm.tasso_taeg),
        provvigione_importo: prevForm.provvigione_importo === '' || prevForm.provvigione_importo == null ? null : parseFloat(prevForm.provvigione_importo),
        provvigione_percentuale: prevForm.provvigione_percentuale === '' || prevForm.provvigione_percentuale == null ? null : parseFloat(prevForm.provvigione_percentuale),
      };
      if (prevForm.id) {
        await api.patch(`/leads/${lead.id}/preventivi/${prevForm.id}`, payload);
        toast.success('Preventivo aggiornato');
      } else {
        await api.post(`/leads/${lead.id}/preventivi`, payload);
        toast.success('Preventivo salvato');
      }
      setPrevForm(null);
      onLeadRefresh && onLeadRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSavingPrev(false); }
  };
  const deletePrev = async (pid) => {
    if (!window.confirm('Eliminare questo preventivo?')) return;
    try {
      await api.delete(`/leads/${lead.id}/preventivi/${pid}`);
      toast.success('Preventivo eliminato');
      onLeadRefresh && onLeadRefresh();
    } catch (e) { toast.error('Errore eliminazione'); }
  };
  const confirmPrev = async (pid) => {
    if (!window.confirm('Confermare questo preventivo? Verra\' creata automaticamente una pratica.')) return;
    setConfirmingId(pid);
    try {
      const r = await api.post(`/leads/${lead.id}/preventivi/${pid}/conferma`);
      const praticaId = r.data?.pratica_id;
      const praticaType = r.data?.pratica_type;
      const targetPath = praticaType === 'prestito' ? '/prestiti' : '/richieste-emissione';
      toast.success(r.data.message || 'Preventivo confermato', {
        description: praticaId ? `Pratica #${praticaId} creata` : undefined,
        action: praticaId ? { label: 'Apri pratica', onClick: () => { window.location.href = targetPath; } } : undefined,
        duration: 8000,
      });
      onLeadRefresh && onLeadRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore conferma');
    } finally { setConfirmingId(null); }
  };

  return (
    <>
      <DialogHeader>
        <div className="flex items-center justify-between">
          <div>
            <DialogTitle className="text-lg">{lead.nome} {lead.cognome}</DialogTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statoStyle.color}`}>{statoStyle.label}</span>
              {lead.interesse && <Badge variant="outline" className="text-[10px]">{lead.interesse}</Badge>}
              {lead.fonte && <span className="text-[10px] text-slate-400">{lead.fonte}</span>}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onEdit} className="text-xs"><Edit2 size={13} className="mr-1" /> Modifica</Button>
        </div>
      </DialogHeader>

      <div className="space-y-4 mt-3">
        {/* Contact info */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          {lead.telefono && (
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <Phone size={14} className="text-slate-400" />
              <span className="text-slate-700 font-medium">{lead.telefono}</span>
            </div>
          )}
          {lead.email && (
            <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
              <Mail size={14} className="text-slate-400" />
              <span className="text-slate-700 font-medium text-xs">{lead.email}</span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 text-xs">
          <div className="flex justify-between py-1 border-b border-slate-100">
            <span className="text-slate-400">Primo contatto</span>
            <span className="text-slate-700">{formatDate(lead.data_primo_contatto)}</span>
          </div>
          <div className="flex justify-between py-1 border-b border-slate-100">
            <span className="text-slate-400">Follow-up</span>
            <span className="text-slate-700 font-medium">{lead.data_followup || '-'}</span>
          </div>
        </div>

        {lead.note && <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">{lead.note}</div>}

        {/* Auto Follow-up Toggle */}
        {isActive && lead.telefono && (
          <div className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-2.5" data-testid="auto-followup-toggle">
            <div className="flex items-center gap-2">
              {autoOn ? <Bell size={14} className="text-green-600" /> : <BellOff size={14} className="text-slate-400" />}
              <div>
                <p className="text-xs font-medium text-slate-700">Auto Follow-up WhatsApp</p>
                <p className="text-[10px] text-slate-400">
                  {autoOn && autoGlobalEnabled ? 'Messaggio automatico alla data di follow-up' : autoOn ? 'Automazione globale disattivata' : 'Disattivato per questo lead'}
                </p>
              </div>
            </div>
            <Switch checked={autoOn} onCheckedChange={onToggleAuto} data-testid="auto-followup-switch" />
          </div>
        )}

        {/* Cronologia WhatsApp */}
        {lead.telefono && (
          <WhatsAppConversation phone={lead.telefono} />
        )}

        {/* WhatsApp Section */}
        {isActive && lead.telefono && (
          <div className="border border-green-200 rounded-xl p-4 bg-green-50/30">
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
              <MessageCircle size={16} className="text-green-600" /> Invia Follow-up WhatsApp
            </h3>
            <div className="space-y-3">
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="text-xs text-slate-500">Template messaggio</Label>
                  <select value={selectedTemplate} onChange={e => setSelectedTemplate(e.target.value)}
                    className="w-full mt-1 px-2 py-1.5 border border-slate-200 rounded-md text-xs bg-white" data-testid="wa-template-select">
                    {TEMPLATES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    <option value="custom">Messaggio personalizzato</option>
                  </select>
                </div>
                <Button size="sm" onClick={() => onSendWA(selectedTemplate === 'custom' ? 'default' : selectedTemplate, selectedTemplate === 'custom' ? customMsg : null)}
                  disabled={sendingWA || (selectedTemplate === 'custom' && !customMsg.trim())}
                  className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="send-wa-btn">
                  {sendingWA ? <Loader2 size={13} className="animate-spin mr-1" /> : <Send size={13} className="mr-1" />}
                  Invia
                </Button>
              </div>
              {selectedTemplate === 'custom' && (
                <Textarea value={customMsg} onChange={e => setCustomMsg(e.target.value)} rows={2}
                  placeholder="Scrivi il messaggio personalizzato..." className="text-xs" data-testid="wa-custom-msg" />
              )}
            </div>
          </div>
        )}

        {/* Convert actions */}
        {isActive && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onConvertPratica} className="text-xs flex-1" data-testid="convert-pratica-btn">
              <Briefcase size={13} className="mr-1" /> Converti in Pratica
            </Button>
            <Button size="sm" variant="outline" onClick={onConvertPreventivo} className="text-xs flex-1" data-testid="convert-preventivo-btn">
              <FileText size={13} className="mr-1" /> Converti in Preventivo
            </Button>
          </div>
        )}

        {/* Preventivi del Lead */}
        <div className="border border-amber-200 rounded-xl p-3 bg-amber-50/30">
          <div className="flex items-center justify-between mb-2">
            <button onClick={() => setShowPreventivi(!showPreventivi)} className="flex items-center gap-2 text-sm font-semibold text-slate-800" data-testid="toggle-preventivi">
              <FileText size={14} className="text-amber-600" /> Preventivi ({preventivi.length})
              {showPreventivi ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </button>
            {isActive && <Button size="sm" variant="outline" onClick={openNewPrev} className="text-[11px] h-7" data-testid="add-preventivo-btn"><Plus size={12} className="mr-1" /> Nuovo</Button>}
          </div>
          {showPreventivi && (
            <div className="space-y-2">
              {preventivi.length === 0 && !prevForm && (
                <p className="text-[11px] text-slate-400 italic text-center py-2">Nessun preventivo salvato per questo lead</p>
              )}
              {preventivi.map(p => (
                <div key={p.id} className="bg-white border border-amber-100 rounded-lg p-2.5 text-xs" data-testid={`preventivo-row-${p.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800">{p.titolo || p.ramo || 'Preventivo'}</span>
                      {p.compagnia && <Badge variant="outline" className="text-[9px]">{p.compagnia}</Badge>}
                      <Badge className={`text-[9px] border-0 ${p.stato === 'confermato' ? 'bg-emerald-100 text-emerald-700' : p.stato === 'rifiutato' ? 'bg-red-100 text-red-700' : p.stato === 'inviato' ? 'bg-sky-100 text-sky-700' : 'bg-slate-200 text-slate-600'}`}>{p.stato}</Badge>
                    </div>
                    <div className="flex gap-1">
                      {p.stato !== 'confermato' && (
                        <>
                          <button onClick={() => setPrevForm({ ...p, importo: p.importo ?? '', importo_rata: p.importo_rata ?? '' })} className="p-1 rounded hover:bg-slate-100" title="Modifica"><Edit2 size={11} className="text-slate-500" /></button>
                          <button onClick={() => deletePrev(p.id)} className="p-1 rounded hover:bg-red-50" title="Elimina"><Trash2 size={11} className="text-red-500" /></button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                    {p.targa && <div><span className="text-slate-400">Targa:</span> <b>{p.targa}</b></div>}
                    {p.ramo && <div><span className="text-slate-400">Tipo:</span> {p.ramo}</div>}
                    {p.durata_mesi && <div><span className="text-slate-400">Durata:</span> {p.durata_mesi} mesi</div>}
                    {(p.importo || p.importo === 0) && <div><span className="text-slate-400">Importo:</span> € {Number(p.importo).toFixed(2)}</div>}
                    {p.importo_rata && <div><span className="text-slate-400">Rata:</span> € {Number(p.importo_rata).toFixed(2)} {p.frequenza_pagamento}</div>}
                    {p.valido_fino && <div><span className="text-slate-400">Valido fino:</span> {p.valido_fino}</div>}
                  </div>
                  {p.garanzie && <p className="text-[11px] text-slate-500 mt-1"><span className="text-slate-400">Garanzie:</span> {p.garanzie}</p>}
                  {p.note && <p className="text-[11px] text-slate-500 mt-1 italic">{p.note}</p>}
                  {p.stato === 'confermato' && (
                    <div className="mt-1.5 flex items-center gap-1 text-[10px] text-emerald-700">
                      <CheckCircle size={11} /> Confermato {p.confermato_at ? `il ${formatDate(p.confermato_at)}` : ''} da {p.confermato_da || '-'} → Pratica #{p.pratica_id}
                    </div>
                  )}
                  {p.stato !== 'confermato' && isActive && (
                    <Button size="sm" onClick={() => confirmPrev(p.id)} disabled={confirmingId === p.id}
                      className="mt-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] h-7" data-testid={`confirm-preventivo-${p.id}`}>
                      {confirmingId === p.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <CheckCircle size={12} className="mr-1" />}
                      Conferma e crea pratica
                    </Button>
                  )}
                </div>
              ))}
              {prevForm && (
                <div className="bg-white border-2 border-amber-300 rounded-lg p-3 space-y-2">
                  <h4 className="text-xs font-semibold text-slate-700">{prevForm.id ? 'Modifica preventivo' : 'Nuovo preventivo'}</h4>
                  {(() => {
                    const isCreditoLead = isInteresseCredito(lead.interesse);
                    const opzioniRamo = isCreditoLead
                      ? ['Cessione Quinto Pensione', 'Cessione Quinto Stipendio', 'Prestito Personale', 'Finanziamento', 'Altro Credito']
                      : ['RCA Auto', 'Polizza Casa', 'Polizza Vita', 'Polizza Salute', 'Polizza Infortuni', 'Polizza Salute', 'Altro Assicurativo'];
                    const isPreventivoCredito = ['Cessione Quinto Pensione', 'Cessione Quinto Stipendio', 'Prestito Personale', 'Finanziamento', 'Altro Credito'].includes(prevForm.ramo);
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder={isCreditoLead ? "Titolo (es. CQS Compass)" : "Titolo (es. Auto Allianz)"} value={prevForm.titolo || ''} onChange={e => setPrevForm({ ...prevForm, titolo: e.target.value })} className="text-xs h-8" data-testid="prev-titolo" />
                          <Input placeholder={isCreditoLead ? "Banca/Finanziaria" : "Compagnia"} value={prevForm.compagnia || ''} onChange={e => setPrevForm({ ...prevForm, compagnia: e.target.value })} className="text-xs h-8" />
                          <select value={prevForm.ramo || ''} onChange={e => setPrevForm({ ...prevForm, ramo: e.target.value })} className="text-xs h-8 px-2 border border-slate-200 rounded-md bg-white" data-testid="prev-ramo">
                            <option value="">-- Tipo --</option>
                            {opzioniRamo.map(r => <option key={r}>{r}</option>)}
                          </select>
                          {!isPreventivoCredito && (
                            <Input placeholder="Targa (se auto)" value={prevForm.targa || ''} onChange={e => setPrevForm({ ...prevForm, targa: e.target.value.toUpperCase() })} className="text-xs h-8 uppercase" />
                          )}
                          {isPreventivoCredito && (
                            <Input type="number" placeholder="Durata (mesi)" value={prevForm.durata_mesi} onChange={e => setPrevForm({ ...prevForm, durata_mesi: e.target.value })} className="text-xs h-8" data-testid="prev-durata" />
                          )}
                          <Input type="number" step="0.01" placeholder={isPreventivoCredito ? "Importo erogato €" : "Premio €"} value={prevForm.importo} onChange={e => setPrevForm({ ...prevForm, importo: e.target.value })} className="text-xs h-8" data-testid="prev-importo" />
                          <Input type="number" step="0.01" placeholder={isPreventivoCredito ? "Rata mensile €" : "Importo rata €"} value={prevForm.importo_rata} onChange={e => setPrevForm({ ...prevForm, importo_rata: e.target.value })} className="text-xs h-8" />
                          {!isPreventivoCredito && (
                            <select value={prevForm.frequenza_pagamento || ''} onChange={e => setPrevForm({ ...prevForm, frequenza_pagamento: e.target.value })} className="text-xs h-8 px-2 border border-slate-200 rounded-md bg-white">
                              <option value="">Frequenza</option>
                              <option>Annuale</option><option>Semestrale</option><option>Quadrimestrale</option><option>Mensile</option>
                            </select>
                          )}
                          <Input type="date" placeholder="Valido fino" value={prevForm.valido_fino || ''} onChange={e => setPrevForm({ ...prevForm, valido_fino: e.target.value })} className="text-xs h-8" />
                        </div>
                        {!isPreventivoCredito && (
                          <Input placeholder="Garanzie (es. Furto, Incendio...)" value={prevForm.garanzie || ''} onChange={e => setPrevForm({ ...prevForm, garanzie: e.target.value })} className="text-xs h-8" />
                        )}
                        {isPreventivoCredito && (
                          <div className="grid grid-cols-2 gap-2">
                            <Input type="number" step="0.01" placeholder="TAN %" value={prevForm.tasso_tan || ''} onChange={e => setPrevForm({ ...prevForm, tasso_tan: e.target.value })} className="text-xs h-8" data-testid="prev-tan" />
                            <Input type="number" step="0.01" placeholder="TAEG %" value={prevForm.tasso_taeg || ''} onChange={e => setPrevForm({ ...prevForm, tasso_taeg: e.target.value })} className="text-xs h-8" data-testid="prev-taeg" />
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100">
                          <Input placeholder="Segnalatore (opzionale)" value={prevForm.segnalato_da || ''} onChange={e => setPrevForm({ ...prevForm, segnalato_da: e.target.value })} className="text-xs h-8" data-testid="prev-segnalatore" />
                          <Input type="number" step="0.01" placeholder="Provvigione EUR" value={prevForm.provvigione_importo || ''} onChange={e => setPrevForm({ ...prevForm, provvigione_importo: e.target.value })} className="text-xs h-8" data-testid="prev-provvigione" />
                        </div>
                        <p className="text-[10px] text-slate-500">Se il segnalatore è vuoto, la provvigione andrà all'Agenzia</p>
                      </>
                    );
                  })()}
                  <Textarea placeholder="Note" rows={2} value={prevForm.note || ''} onChange={e => setPrevForm({ ...prevForm, note: e.target.value })} className="text-xs" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={savePrev} disabled={savingPrev} className="flex-1 text-xs h-8 bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-preventivo-btn">
                      {savingPrev ? <Loader2 size={12} className="animate-spin mr-1" /> : <Save size={12} className="mr-1" />} Salva
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPrevForm(null)} className="text-xs h-8">Annulla</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Message history */}
        {lead.messaggi_inviati?.length > 0 && (
          <div>
            <button onClick={() => setShowHistory(!showHistory)} className="flex items-center gap-2 text-xs font-semibold text-slate-600 mb-2" data-testid="toggle-history">
              {showHistory ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Storico messaggi ({lead.messaggi_inviati.length})
            </button>
            {showHistory && (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {lead.messaggi_inviati.slice().reverse().map((m, i) => (
                  <div key={i} className="bg-slate-50 rounded-lg px-3 py-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-slate-400">{formatDate(m.inviato_at)} - {m.inviato_da}</span>
                        {m.auto && (
                          <Badge className="bg-blue-100 text-blue-600 text-[8px] border-0 px-1 py-0">
                            <Zap size={8} className="mr-0.5" /> Auto
                          </Badge>
                        )}
                      </div>
                      <Badge variant={m.success ? 'default' : 'destructive'} className="text-[9px]">
                        {m.success ? 'Inviato' : 'Errore'}
                      </Badge>
                    </div>
                    <p className="text-slate-600 line-clamp-2">{m.messaggio}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
